import type { ReactNode } from 'react';
import styles from './Surface.module.css';

/**
 * The four elevation layers of design system § 8.
 *
 * Elevation is a closed set, not a number: L0 is the page, L1 a content
 * surface, L2 an interactive one, L3 the single hero a screen is allowed. A
 * surface never invents a shadow — § 8 defines exactly four, all hue-tinted
 * green and never black.
 */

export type SurfaceLayer = 0 | 1 | 2 | 3;
export type SurfaceRadius = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';

interface SurfaceProps {
  layer?: SurfaceLayer;
  radius?: SurfaceRadius;
  /** Applies the standard § 7 inset. Off by default: padding is a layout choice. */
  inset?: boolean;
  /**
   * Marks the surface as carrying the § 8 hover and press behaviour. Static
   * surfaces must not: "everything touchable looks touchable" (§ 1.9) only
   * holds if the reverse does too.
   */
  interactive?: boolean;
  /**
   * The element the surface IS. Sectioning and grouping elements only: a
   * surface is a place content sits, never a control — `interactive` marks
   * one that responds to a pointer, and the control itself is still a
   * `Button` inside it.
   */
  as?: 'div' | 'section' | 'article' | 'li' | 'aside' | 'header' | 'footer';
  children: ReactNode;
}

export function Surface({
  layer = 1,
  radius = 'lg',
  inset = false,
  interactive = false,
  as: Component = 'div',
  children,
}: SurfaceProps) {
  return (
    <Component
      className={styles.surface}
      data-layer={layer}
      data-radius={radius}
      data-inset={inset || undefined}
      data-interactive={interactive || undefined}
    >
      {children}
    </Component>
  );
}
